import React, { useMemo, useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Plus, Upload, ArrowLeft, Loader2, Download, FileCheck2, MessageSquare, Table, X, Play, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface OtherIncomeNetInvestmentOperatingLeaseProps {
  onBack?: () => void;
}

type PaymentRow = {
  payment_type: string;
  payment_amount: number | '';
  n_payments: number | '';
  int_incr: string; // comma-separated numbers
  incr_per: string; // comma-separated numbers/percentages
  month_first_payment: number | '';
};

type Lease = {
  lease_name: string;
  commencement_date: string; // yyyy-mm-dd from input
  lease_period: number | '';
  payment_cycle: "Monthly" | "Yearly" | '';
  payment_details: PaymentRow[];
};

interface LedgerAccount {
  ledger_id: number;
  ledger_name: string;
  closing_balance: number;
  note_line_id: number;
}

interface UserReconciliation {
  reason: string;
  amount: number;
}

export default function OtherIncomeNetInvestmentOperatingLease({ onBack }: OtherIncomeNetInvestmentOperatingLeaseProps) {
  const [leases, setLeases] = useState<Lease[]>([]);
  const [, setRunning] = useState(false);
  const [globalSaveStatus, setGlobalSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const financeGlobalSaveRef = useRef<null | (() => Promise<void>)>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningAssessment, setIsRunningAssessment] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  
  // Operating Lease Reconciliation state
  const [opLiabilityAsPerWorkings, setOpLiabilityAsPerWorkings] = useState<number>(0);
  const [opSelectedLedgerAccounts, setOpSelectedLedgerAccounts] = useState<number[]>([]);
  const [opAvailableLedgerAccounts, setOpAvailableLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [opIsLoadingLedgerAccounts, setOpIsLoadingLedgerAccounts] = useState(false);
  const [opUserReconciliations, setOpUserReconciliations] = useState<UserReconciliation[]>([]);
  const [opIsSavingReconciliation, setOpIsSavingReconciliation] = useState(false);
  const [opIsLoadingReconciliation, setOpIsLoadingReconciliation] = useState(false);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `OTHER_INCOME_NET_INVESTMENT_${normalizedSection}`;
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_OI_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_OI_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "NET-INVESTMENT-IN-LEASES-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger read on mount
  useEffect(() => {
    downloadInputs();
    loadOpLedgerAccounts();
    loadOpReconciliationData();
  }, []);

  // Load Operating Lease ledger accounts: AS → fs_sub_line 20027, Ind AS → 20029
  const loadOpLedgerAccounts = async () => {
    setOpIsLoadingLedgerAccounts(true);
    try {
      let targetFsSubLineId = 20027;
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? frfResult.frf : null;
        if (frf === "Indian Accounting Standards (Ind AS)") {
          targetFsSubLineId = 20029;
        }
      } catch {
        // keep AS default
      }

      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const accounts: LedgerAccount[] = [];

          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                const fsSubLineId = item.fs_sub_line_id || item.fsSubLineId;
                
                if (fsSubLineId === targetFsSubLineId) {
                  const amountDict = item.amount || {};
                  
                  if (!amountDict || typeof amountDict !== 'object' || Object.keys(amountDict).length === 0) {
                    return;
                  }

                  const parseDate = (dateStr: string): Date => {
                    const [day, month, year] = dateStr.split('/');
                    return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
                  };

                  const dates = Object.keys(amountDict);
                  const latestDate = dates.reduce((latest, current) => {
                    try {
                      const latestDateObj = parseDate(latest);
                      const currentDateObj = parseDate(current);
                      return currentDateObj > latestDateObj ? current : latest;
                    } catch {
                      return latest;
                    }
                  }, dates[0]);

                  const latestAmount = parseFloat(amountDict[latestDate]) || 0;
                  let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                  ledgerName = ledgerName
                    .replace(/_x000D/g, '')
                    .replace(/\n/g, '')
                    .replace(/_+$/, '')
                    .trim();

                  accounts.push({
                    ledger_id: item.ledger_id || item.ledgerId || 0,
                    ledger_name: ledgerName,
                    closing_balance: latestAmount,
                    note_line_id: item.note_line_id || item.noteLineId || 0
                  });
                }
              });
            }
          }

          const uniqueAccounts = accounts.reduce((acc, account) => {
            const existing = acc.find(a => a.ledger_id === account.ledger_id);
            if (!existing) {
              acc.push(account);
            } else {
              existing.closing_balance += account.closing_balance;
            }
            return acc;
          }, [] as LedgerAccount[]);

          setOpAvailableLedgerAccounts(uniqueAccounts);
        }
      }
    } catch (error) {
      console.error('Error loading Operating Lease ledger accounts:', error);
    } finally {
      setOpIsLoadingLedgerAccounts(false);
    }
  };

  // Load Operating Lease reconciliation data from Execution_OTINC_OprResponse.json
  const loadOpReconciliationData = async () => {
    setOpIsLoadingReconciliation(true);
    try {
      // Try to download using downloadClientJson which may work for juggernaut folder files
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson('Execution_OTINC_OprResponse.json');
        if (result && typeof result === 'object') {
          // Handle both direct data and result object with data property
          const data = (result as any).data || result;
          if (data && typeof data === 'object') {
            setOpLiabilityAsPerWorkings(data.liabilityAsPerWorkings || 0);
            setOpSelectedLedgerAccounts(data.selectedLedgerAccounts || []);
            setOpUserReconciliations(data.userReconciliations || []);
          }
        }
      }
    } catch (error) {
      // File doesn't exist yet, which is fine - we'll create it on save
      console.log('Operating Lease reconciliation file not found, will be created on save');
    } finally {
      setOpIsLoadingReconciliation(false);
    }
  };

  // Save Operating Lease reconciliation data to Execution_OTINC_OprResponse.json
  const saveOpReconciliationData = async () => {
    setOpIsSavingReconciliation(true);
    try {
      const data = {
        liabilityAsPerWorkings: opLiabilityAsPerWorkings,
        selectedLedgerAccounts: opSelectedLedgerAccounts,
        userReconciliations: opUserReconciliations
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTINC_OprResponse.json',
          referenceValue: 'Other Income Operating Lease Reconciliation',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTINC_OprResponse']
        });
        if (!result.success) {
          console.error('Failed to save Operating Lease reconciliation data:', result.error || 'Unknown error occurred');
          throw new Error(result.error || 'Unknown error occurred');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving Operating Lease reconciliation data:', error);
      throw error;
    } finally {
      setOpIsSavingReconciliation(false);
    }
  };

  // Calculate total closing balance for Operating Lease
  const getOpTotalClosingBalance = (): number => {
    return opSelectedLedgerAccounts.reduce((total, ledgerId) => {
      const account = opAvailableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
      return total + (account?.closing_balance || 0);
    }, 0);
  };

  // Calculate difference for Operating Lease
  const getOpDifference = (): number => {
    return opLiabilityAsPerWorkings - getOpTotalClosingBalance();
  };

  // Calculate net difference for Operating Lease
  const getOpNetDifference = (): number => {
    const difference = getOpDifference();
    const reconciliationTotal = opUserReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);
    return difference - reconciliationTotal;
  };

  // Add Operating Lease user reconciliation row
  const addOpUserReconciliation = () => {
    setOpUserReconciliations([...opUserReconciliations, { reason: '', amount: 0 }]);
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "OI";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "NET-INVESTMENT-IN-LEASES-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  // Remove Operating Lease user reconciliation row
  const removeOpUserReconciliation = (index: number) => {
    setOpUserReconciliations(opUserReconciliations.filter((_, i) => i !== index));
  };

  // Update Operating Lease user reconciliation
  const updateOpUserReconciliation = (index: number, field: 'reason' | 'amount', value: string | number) => {
    setOpUserReconciliations(opUserReconciliations.map((rec, i) => 
      i === index ? { ...rec, [field]: value } : rec
    ));
  };

  const addLease = () => {
    setLeases((prev) => [
      ...prev,
      {
        lease_name: "",
        commencement_date: "",
        lease_period: "",
        payment_cycle: "",
        payment_details: [
          { payment_type: "", payment_amount: "", n_payments: "", int_incr: "", incr_per: "", month_first_payment: "" },
        ],
      },
    ]);
  };

  // Run Operating Lease (Other Income) - executes Python without inputs
  const runOperatingLease = async () => {
    setIsRunningAssessment(true);
    try {
      // First save the latest Operating Lease input JSON
      await uploadJson();

      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_othinc_operating_lease", {
          inputFiles: [],
          options: { reference_value: "Other Income Operating Lease" }
        });

        if (result.ok) {
          alert(' Operating Lease run completed successfully! Excel file has been generated and uploaded to SharePoint.');
          setIsRunningAssessment(false);
        } else {
          throw new Error(result.error || 'Failed to start Operating Lease run');
        }
      } else {
        throw new Error('Payroll API not available');
      }
    } catch (error) {
      console.error('Error running Operating Lease:', error);
      alert(`Error running Operating Lease: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      setIsRunningAssessment(false);
    }
  };

  const updateLeaseField = (idx: number, field: keyof Lease, value: any) => {
    setLeases((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addPaymentRow = (leaseIdx: number) => {
    setLeases((prev) =>
      prev.map((l, i) =>
        i === leaseIdx
          ? {
              ...l,
              payment_details: [
                ...l.payment_details,
                { payment_type: "", payment_amount: "", n_payments: "", int_incr: "", incr_per: "", month_first_payment: "" },
              ],
            }
          : l,
      ),
    );
  };

  const updatePaymentRow = (leaseIdx: number, rowIdx: number, field: keyof PaymentRow, value: any) => {
    setLeases((prev) =>
      prev.map((l, i) =>
        i === leaseIdx
          ? {
              ...l,
              payment_details: l.payment_details.map((r, ri) => (ri === rowIdx ? { ...r, [field]: value } : r)),
            }
          : l,
      ),
    );
  };

  const payload = useMemo(() => {
    const mapped = leases.map((l) => ({
      lease_name: l.lease_name,
      commencement_date: l.commencement_date
        ? new Date(l.commencement_date).toLocaleDateString("en-US")
        : "",
      lease_period: typeof l.lease_period === "number" ? l.lease_period : Number(l.lease_period || 0),
      payment_cycle: l.payment_cycle || "Monthly",
      payment_details: l.payment_details.map((r) => ({
        payment_type: r.payment_type,
        payment_amount: Number(r.payment_amount || 0),
        n_payments: Number(r.n_payments || 0),
        int_incr: r.int_incr
          ? r.int_incr
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              .map((s) => Number(s))
          : [],
        incr_per: r.incr_per
          ? r.incr_per
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              .map((s) => Number(s))
          : [],
        month_first_payment: Number(r.month_first_payment || 0),
      })),
    }));

    return {
      leases: mapped,
    };
  }, [leases]);

  // Download JSON from SharePoint and load inputs (same flow as Lease Liability Operating Lease)
  const downloadInputs = async () => {
    setIsLoading(true);
    try {
      if (window.sharePointAPI?.downloadOthIncOpInput) {
        const result = await window.sharePointAPI.downloadOthIncOpInput();
        if (result.success && result.data) {
          const incoming = Array.isArray(result.data.leases) ? result.data.leases : [];
          const mapped: Lease[] = incoming.map((lease: any) => ({
            lease_name: lease.lease_name || "",
            commencement_date: lease.commencement_date ? new Date(lease.commencement_date).toISOString().slice(0, 10) : "",
            lease_period: typeof lease.lease_period === "number" ? lease.lease_period : Number(lease.lease_period || 0),
            payment_cycle: (lease.payment_cycle as any) === "Yearly" ? "Yearly" : "Monthly",
            payment_details: (lease.payment_details || []).map((detail: any) => ({
              payment_type: detail.payment_type || "",
              payment_amount: Number(detail.payment_amount || 0),
              n_payments: Number(detail.n_payments || 0),
              int_incr: Array.isArray(detail.int_incr) ? detail.int_incr.join(",") : (detail.int_incr || ""),
              incr_per: Array.isArray(detail.incr_per) ? detail.incr_per.join(",") : (detail.incr_per || ""),
              month_first_payment: Number(detail.month_first_payment || 0),
            })),
          }));
          setLeases(mapped);
        } else {
          throw new Error(result.error || 'Failed to download inputs');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error downloading Other Income Operating Lease inputs:', error);
      alert(`Error downloading inputs: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadJson = async () => {
    setRunning(true);
    try {
      const jsonString = JSON.stringify(payload, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTINC_OpInp.json',
          referenceValue: 'Other Income Operating Lease Input',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTINC_OpInp']
        });
        if (!result.success) {
          console.error('Failed to save Operating Lease JSON:', result.error || 'Unknown error occurred');
          throw new Error(result.error || 'Unknown error occurred');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error uploading Operating Lease JSON:', error);
      throw error;
    } finally {
      setRunning(false);
    }
  };

  const registerFinanceGlobalSave = (fn: () => Promise<void>) => {
    financeGlobalSaveRef.current = fn;
  };

  const handleGlobalSave = async () => {
    if (globalSaveStatus === 'saving') return;
    setGlobalSaveStatus('saving');
    try {
      // 1. Save Operating Lease inputs
      await uploadJson();
      // 2. Save Operating Lease reconciliation
      await saveOpReconciliationData();
      // 3. Trigger Finance Lease section saves (inputs, reconciliation, AS 19, inference)
      if (financeGlobalSaveRef.current) {
        await financeGlobalSaveRef.current();
      }
      // 4. Save Net Investment inference
      await saveInferenceQuestion();

      setGlobalSaveStatus('saved');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Global save failed for Net Investment in Leases - Operating Lease:', error);
      setGlobalSaveStatus('error');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    }
  };

  return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Net Investment in Leases - Operating Lease</h2>
            <p className="text-sm text-white/60">Add leases and payment schedules, then upload JSON.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleGlobalSave}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={globalSaveStatus === 'saving'}
            >
              {globalSaveStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            {globalSaveStatus === 'saved' && (
              <span className="text-sm text-green-400">Saved!</span>
            )}
            {globalSaveStatus === 'error' && (
              <span className="text-sm text-red-400">Error</span>
            )}
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            )}
          </div>
        </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-white">Operating Lease</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Operating Lease"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={addLease} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" /> Add Lease
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {leases.map((lease, li) => (
            <div key={li} className="rounded-md border border-white/10 p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-white">Name of Lease</Label>
                  <Input value={lease.lease_name} onChange={(e) => updateLeaseField(li, "lease_name", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white">Lease Commencement Date</Label>
                  <Input type="date" value={lease.commencement_date} onChange={(e) => updateLeaseField(li, "commencement_date", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white">Lease Period (in months)</Label>
                  <Input type="number" value={lease.lease_period as any} onChange={(e) => updateLeaseField(li, "lease_period", e.target.value ? Number(e.target.value) : "")} className="border-white/10 bg-black/40 text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white">Payment Cycle</Label>
                  <Select value={lease.payment_cycle as any} onValueChange={(v) => updateLeaseField(li, "payment_cycle", v as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-white/90 text-sm font-medium">Payment Schedule</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-white/70">
                      <tr>
                        <th className="px-2 py-2 text-left">Payment Type</th>
                        <th className="px-2 py-2 text-left">Payment Amount (per month)</th>
                        <th className="px-2 py-2 text-left">No. of months</th>
                        <th className="px-2 py-2 text-left">Interval of Increment</th>
                        <th className="px-2 py-2 text-left">Increment %</th>
                        <th className="px-2 py-2 text-left">Month of first payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lease.payment_details.map((r, ri) => (
                        <tr key={ri} className="border-t border-white/10">
                          <td className="px-2 py-2"><Input value={r.payment_type} onChange={(e) => updatePaymentRow(li, ri, "payment_type", e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                          <td className="px-2 py-2"><Input type="number" value={r.payment_amount as any} onChange={(e) => updatePaymentRow(li, ri, "payment_amount", e.target.value ? Number(e.target.value) : "")} className="border-white/10 bg-black/40 text-white" /></td>
                          <td className="px-2 py-2"><Input type="number" value={r.n_payments as any} onChange={(e) => updatePaymentRow(li, ri, "n_payments", e.target.value ? Number(e.target.value) : "")} className="border-white/10 bg-black/40 text-white" /></td>
                          <td className="px-2 py-2"><Input placeholder="e.g., 10,15,18" value={r.int_incr} onChange={(e) => updatePaymentRow(li, ri, "int_incr", e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                          <td className="px-2 py-2"><Input placeholder="e.g., 2,3,5" value={r.incr_per} onChange={(e) => updatePaymentRow(li, ri, "incr_per", e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                          <td className="px-2 py-2"><Input type="number" value={r.month_first_payment as any} onChange={(e) => updatePaymentRow(li, ri, "month_first_payment", e.target.value ? Number(e.target.value) : "")} className="border-white/10 bg-black/40 text-white" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-white/60">Note: The interval of increments and the increment % have to be separated by single commas. For eg. if the increment happens in the 10th, 15th and 18th months by 2%, 3% and 5% respectively then they should be entered as 10,15,18 and 2,3,5 respectively for that type of payment.</div>
                <Button onClick={() => addPaymentRow(li)} size="sm" variant="outline" className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
                  <Plus className="h-4 w-4 mr-2" /> Add Row
                </Button>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-3 mt-4">
            <Button onClick={runOperatingLease} disabled={isRunningAssessment} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {isRunningAssessment ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...</> : <><FileCheck2 className="h-4 w-4 mr-2" /> Run Operating Lease</>}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Upload and Run Operating Lease"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Operating Lease Liability Reconciliation */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Table className="h-5 w-5" />
              Operating Lease Liability Reconciliation
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={loadOpReconciliationData}
                disabled={opIsLoadingReconciliation}
                size="sm"
                variant="outline"
                className="border-white/10 text-white"
              >
                <Play className="h-4 w-4 mr-2" />
                {opIsLoadingReconciliation ? 'Loading...' : 'Execution'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Operating Lease Liability Reconciliation"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">Operating Lease liability as per workings (A)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <Input
                      type="number"
                      value={opLiabilityAsPerWorkings || ''}
                      onChange={(e) => setOpLiabilityAsPerWorkings(parseFloat(e.target.value) || 0)}
                      className="border-white/10 bg-black/40 text-white text-right"
                      placeholder="Enter amount"
                    />
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">Operating Lease liability as per schedule (B)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <div className="space-y-2">
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (value && !opSelectedLedgerAccounts.includes(parseInt(value))) {
                            setOpSelectedLedgerAccounts([...opSelectedLedgerAccounts, parseInt(value)]);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={opIsLoadingLedgerAccounts ? "Loading..." : "Select ledger accounts"} />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white max-h-60">
                          {opAvailableLedgerAccounts
                            .filter(acc => !opSelectedLedgerAccounts.includes(acc.ledger_id))
                            .map((account) => (
                              <SelectItem key={account.ledger_id} value={account.ledger_id.toString()}>
                                {account.ledger_name} (Note Line ID: {account.note_line_id})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {opSelectedLedgerAccounts.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {opSelectedLedgerAccounts.map((ledgerId) => {
                            const account = opAvailableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
                            return account ? (
                              <div key={ledgerId} className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-sm text-white">
                                <span>{account.ledger_name}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setOpSelectedLedgerAccounts(opSelectedLedgerAccounts.filter(id => id !== ledgerId))}
                                  className="h-4 w-4 p-0 text-white hover:text-red-400"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}
                      <div className="text-right text-white font-semibold">
                        Total: {getOpTotalClosingBalance().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference (C)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getOpDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                {opUserReconciliations.map((rec, index) => (
                  <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      <Input
                        type="text"
                        value={rec.reason}
                        onChange={(e) => updateOpUserReconciliation(index, 'reason', e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="Enter reason"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={rec.amount || ''}
                          onChange={(e) => updateOpUserReconciliation(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="border-white/10 bg-black/40 text-white text-right flex-1"
                          placeholder="Enter amount"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOpUserReconciliation(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getOpNetDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-start items-center">
            <Button
              onClick={addOpUserReconciliation}
              size="sm"
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Finance Lease Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle className="text-white">Finance Lease</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Finance Lease"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <FinanceLeaseSection
            companyName={companyName}
            inferenceQuestion={inferenceQuestion}
            setInferenceQuestion={setInferenceQuestion}
            inferenceSaveStatus={inferenceSaveStatus}
            inferenceSaveError={inferenceSaveError}
            saveInferenceQuestion={saveInferenceQuestion}
            registerFinanceGlobalSave={registerFinanceGlobalSave}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Operating Lease")]: "Operating Lease",
              [getSectionQuestionId("Upload and Run Operating Lease")]: "Upload and Run Operating Lease",
              [getSectionQuestionId("Operating Lease Liability Reconciliation")]: "Operating Lease Liability Reconciliation",
              [getSectionQuestionId("Finance Lease")]: "Finance Lease",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}


interface FinanceLeaseSectionProps {
  companyName: string;
  inferenceQuestion: { textResponse: any; documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }> };
  setInferenceQuestion: React.Dispatch<React.SetStateAction<{ textResponse: any; documents: any[] }>>;
  inferenceSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  inferenceSaveError: string | null;
  saveInferenceQuestion: () => Promise<void>;
  registerFinanceGlobalSave: (fn: () => Promise<void>) => void;
}

function FinanceLeaseSection({
  companyName,
  inferenceQuestion,
  setInferenceQuestion,
  inferenceSaveStatus,
  inferenceSaveError,
  saveInferenceQuestion,
  registerFinanceGlobalSave,
}: FinanceLeaseSectionProps) {
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `OTHER_INCOME_FINANCE_LEASE_${normalizedSection}`;
  };

  // Finance Lease Reconciliation state
  const [finLiabilityAsPerWorkings, setFinLiabilityAsPerWorkings] = useState<number>(0);
  const [finSelectedLedgerAccounts, setFinSelectedLedgerAccounts] = useState<number[]>([]);
  const [finAvailableLedgerAccounts, setFinAvailableLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [finIsLoadingLedgerAccounts, setFinIsLoadingLedgerAccounts] = useState(false);
  const [finUserReconciliations, setFinUserReconciliations] = useState<UserReconciliation[]>([]);
  const [finIsSavingReconciliation, setFinIsSavingReconciliation] = useState(false);
  const [finIsLoadingReconciliation, setFinIsLoadingReconciliation] = useState(false);
  const [finIsRunningLease, setFinIsRunningLease] = useState(false);

  // AS 19 Classification Assessment state
  const [as19Classifications, setAs19Classifications] = useState<Array<{
    leaseName: string;
    conditionI: string;
    conditionII: string;
    conditionIII: string;
    conditionIV: string;
    conditionV: string;
  }>>([]);
  const [isLoadingAs19, setIsLoadingAs19] = useState(false);
  const [isSavingAs19, setIsSavingAs19] = useState(false);

  const AS19_CONDITIONS = [
    {
      id: "i",
      description: "The lease transfers ownership of the asset to the lessee by the end of the lease term."
    },
    {
      id: "ii",
      description: "The lessee has the option to purchase the asset at a price which is expected to be sufficiently lower than the fair value at the date the option becomes exercisable such that, at the inception of the lease, it is reasonably certain that the option will be exercised."
    },
    {
      id: "iii",
      description: "The lease term is for the major part of the economic life of the asset even if title is not transferred."
    },
    {
      id: "iv",
      description: "At the inception of the lease the present value of the minimum lease payments amounts to at least substantially all of the fair value of the leased asset."
    },
    {
      id: "v",
      description: "The leased asset is of a specialised nature such that only the lessee can use it without major modifications being made."
    }
  ];

  type FinCondition = {
    c_i: "Yes" | "No" | "";
    c_ii: "Yes" | "No" | "";
    c_iii: "Yes" | "No" | "";
    c_iv: "Yes" | "No" | "";
    c_v: "Yes" | "No" | "";
  };

  type FinPaymentRow = {
    payment_classification: "Rental" | "Guaranteed Residual Value" | "Unguaranteed Residual Value" | "Purchase Price" | "";
    payment_type: string;
    payment_amount: number | '';
    n_payments: number | '';
    int_incr: string; // comma-separated
    incr_per: string; // comma-separated
    month_first_payment: number | '';
  };

  type FinLease = {
    lease_name: string;
    commencement_date: string; // yyyy-mm-dd for input
    lease_period: number | '';
    int_ror: number | '';
    payment_cycle: "Monthly" | "Yearly" | '';
    conditions: FinCondition;
    payment_details: FinPaymentRow[];
  };

  const [leases, setLeases] = useState<FinLease[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setRunning] = useState(false);

  const addLease = () => {
    setLeases(prev => ([
      ...prev,
      {
        lease_name: "",
        commencement_date: "",
        lease_period: "",
        int_ror: "",
        payment_cycle: "",
        conditions: { c_i: "", c_ii: "", c_iii: "", c_iv: "", c_v: "" },
        payment_details: [
          { payment_classification: "", payment_type: "", payment_amount: "", n_payments: "", int_incr: "", incr_per: "", month_first_payment: "" }
        ]
      }
    ]));
  };

  const updateLeaseField = (idx: number, field: keyof FinLease, value: any) => {
    setLeases(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const updateCondition = (idx: number, key: keyof FinCondition, value: "Yes" | "No" | "") => {
    setLeases(prev => prev.map((l, i) => i === idx ? { ...l, conditions: { ...l.conditions, [key]: value } } : l));
  };

  const addPaymentRow = (leaseIdx: number) => {
    setLeases(prev => prev.map((l, i) => i === leaseIdx ? {
      ...l,
      payment_details: [
        ...l.payment_details,
        { payment_classification: "", payment_type: "", payment_amount: "", n_payments: "", int_incr: "", incr_per: "", month_first_payment: "" }
      ]
    } : l));
  };

  const updatePaymentRow = (leaseIdx: number, rowIdx: number, field: keyof FinPaymentRow, value: any) => {
    setLeases(prev => prev.map((l, i) => i === leaseIdx ? {
      ...l,
      payment_details: l.payment_details.map((r, ri) => ri === rowIdx ? { ...r, [field]: value } : r)
    } : l));
  };

  // Auto-trigger load on mount
  useEffect(() => {
    loadFinanceInputs();
    loadFinLedgerAccounts();
    loadFinReconciliationData();
    loadAs19Classifications();
  }, []);

  // Sync AS 19 classifications with leases when leases change
  useEffect(() => {
    setAs19Classifications(prev => {
      const newClassifications = leases
        .filter(lease => lease.lease_name)
        .map(lease => {
          const existing = prev.find(c => c.leaseName === lease.lease_name);
          if (existing) {
            return existing;
          }
          return {
            leaseName: lease.lease_name,
            conditionI: lease.conditions.c_i || 'No',
            conditionII: lease.conditions.c_ii || 'No',
            conditionIII: lease.conditions.c_iii || 'No',
            conditionIV: lease.conditions.c_iv || 'No',
            conditionV: lease.conditions.c_v || 'No'
          };
        });
      
      // Only update if there are actual changes
      const hasChanges = newClassifications.length !== prev.length ||
        newClassifications.some((newClass, idx) => {
          const oldClass = prev[idx];
          return !oldClass || newClass.leaseName !== oldClass.leaseName;
        });
      
      return hasChanges ? newClassifications : prev;
    });
  }, [leases]);

  // Load AS 19 Classification data from Execution_OTINC_FinOptions.json
  const loadAs19Classifications = async () => {
    setIsLoadingAs19(true);
    try {
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson('Execution_OTINC_FinOptions.json');
        if (result && typeof result === 'object') {
          const data = (result as any).data || result;
          if (data && Array.isArray(data.classifications)) {
            setAs19Classifications(data.classifications);
          }
        }
      }
    } catch (error) {
      console.log('AS 19 Classification file not found, will be created on save');
    } finally {
      setIsLoadingAs19(false);
    }
  };

  // Save AS 19 Classification data to Execution_OTINC_FinOptions.json
  const saveAs19Classifications = async () => {
    setIsSavingAs19(true);
    try {
      const data = {
        classifications: as19Classifications
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTINC_FinOptions.json',
          referenceValue: 'Other Income Finance Lease AS 19 Classification',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTINC_FinOptions']
        });
        if (!result.success) {
          console.error('Failed to save AS 19 Classification Assessment:', result.error || 'Unknown error occurred');
          throw new Error(result.error || 'Unknown error occurred');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving AS 19 Classification:', error);
      throw error;
    } finally {
      setIsSavingAs19(false);
    }
  };

  // Update AS 19 Classification
  const updateAs19Classification = (leaseName: string, condition: string, value: string) => {
    setAs19Classifications(prev => prev.map(c => 
      c.leaseName === leaseName ? { ...c, [condition]: value } : c
    ));
    
    // Also update the lease conditions to keep them in sync
    setLeases(prev => prev.map(lease => {
      if (lease.lease_name === leaseName) {
        const conditionMap: Record<string, keyof FinCondition> = {
          'conditionI': 'c_i',
          'conditionII': 'c_ii',
          'conditionIII': 'c_iii',
          'conditionIV': 'c_iv',
          'conditionV': 'c_v'
        };
        const conditionKey = conditionMap[condition];
        if (conditionKey) {
          return {
            ...lease,
            conditions: { ...lease.conditions, [conditionKey]: value as "Yes" | "No" | "" }
          };
        }
      }
      return lease;
    }));
  };

  // Load Finance Lease ledger accounts using fs_sub_line_id 20027
  const loadFinLedgerAccounts = async () => {
    setFinIsLoadingLedgerAccounts(true);
    try {
      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const accounts: LedgerAccount[] = [];
          const TARGET_FS_SUB_LINE_ID = 20027;

          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                const fsSubLineId = item.fs_sub_line_id || item.fsSubLineId;
                
                if (fsSubLineId === TARGET_FS_SUB_LINE_ID) {
                  const amountDict = item.amount || {};
                  
                  if (!amountDict || typeof amountDict !== 'object' || Object.keys(amountDict).length === 0) {
                    return;
                  }

                  const parseDate = (dateStr: string): Date => {
                    const [day, month, year] = dateStr.split('/');
                    return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
                  };

                  const dates = Object.keys(amountDict);
                  const latestDate = dates.reduce((latest, current) => {
                    try {
                      const latestDateObj = parseDate(latest);
                      const currentDateObj = parseDate(current);
                      return currentDateObj > latestDateObj ? current : latest;
                    } catch {
                      return latest;
                    }
                  }, dates[0]);

                  const latestAmount = parseFloat(amountDict[latestDate]) || 0;
                  let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                  ledgerName = ledgerName
                    .replace(/_x000D/g, '')
                    .replace(/\n/g, '')
                    .replace(/_+$/, '')
                    .trim();

                  accounts.push({
                    ledger_id: item.ledger_id || item.ledgerId || 0,
                    ledger_name: ledgerName,
                    closing_balance: latestAmount,
                    note_line_id: item.note_line_id || item.noteLineId || 0
                  });
                }
              });
            }
          }

          const uniqueAccounts = accounts.reduce((acc, account) => {
            const existing = acc.find(a => a.ledger_id === account.ledger_id);
            if (!existing) {
              acc.push(account);
            } else {
              existing.closing_balance += account.closing_balance;
            }
            return acc;
          }, [] as LedgerAccount[]);

          setFinAvailableLedgerAccounts(uniqueAccounts);
        }
      }
    } catch (error) {
      console.error('Error loading Finance Lease ledger accounts:', error);
    } finally {
      setFinIsLoadingLedgerAccounts(false);
    }
  };

  // Load Finance Lease reconciliation data from Execution_OTINC_FinResponse.json
  const loadFinReconciliationData = async () => {
    setFinIsLoadingReconciliation(true);
    try {
      // Try to download using downloadClientJson which may work for juggernaut folder files
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson('Execution_OTINC_FinResponse.json');
        if (result && typeof result === 'object') {
          // Handle both direct data and result object with data property
          const data = (result as any).data || result;
          if (data && typeof data === 'object') {
            setFinLiabilityAsPerWorkings(data.liabilityAsPerWorkings || 0);
            setFinSelectedLedgerAccounts(data.selectedLedgerAccounts || []);
            setFinUserReconciliations(data.userReconciliations || []);
          }
        }
      }
    } catch (error) {
      // File doesn't exist yet, which is fine - we'll create it on save
      console.log('Finance Lease reconciliation file not found, will be created on save');
    } finally {
      setFinIsLoadingReconciliation(false);
    }
  };

  // Save Finance Lease reconciliation data to Execution_OTINC_FinResponse.json
  const saveFinReconciliationData = async () => {
    setFinIsSavingReconciliation(true);
    try {
      const data = {
        liabilityAsPerWorkings: finLiabilityAsPerWorkings,
        selectedLedgerAccounts: finSelectedLedgerAccounts,
        userReconciliations: finUserReconciliations
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTINC_FinResponse.json',
          referenceValue: 'Other Income Finance Lease Reconciliation',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTINC_FinResponse']
        });
        if (!result.success) {
          console.error('Failed to save Finance Lease reconciliation data:', result.error || 'Unknown error occurred');
          throw new Error(result.error || 'Unknown error occurred');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving Finance Lease reconciliation data:', error);
      throw error;
    } finally {
      setFinIsSavingReconciliation(false);
    }
  };

  // Calculate total closing balance for Finance Lease
  const getFinTotalClosingBalance = (): number => {
    return finSelectedLedgerAccounts.reduce((total, ledgerId) => {
      const account = finAvailableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
      return total + (account?.closing_balance || 0);
    }, 0);
  };

  // Calculate difference for Finance Lease
  const getFinDifference = (): number => {
    return finLiabilityAsPerWorkings - getFinTotalClosingBalance();
  };

  // Calculate net difference for Finance Lease
  const getFinNetDifference = (): number => {
    const difference = getFinDifference();
    const reconciliationTotal = finUserReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);
    return difference - reconciliationTotal;
  };

  // Add Finance Lease user reconciliation row
  const addFinUserReconciliation = () => {
    setFinUserReconciliations([...finUserReconciliations, { reason: '', amount: 0 }]);
  };

  // Remove Finance Lease user reconciliation row
  const removeFinUserReconciliation = (index: number) => {
    setFinUserReconciliations(finUserReconciliations.filter((_, i) => i !== index));
  };

  // Update Finance Lease user reconciliation
  const updateFinUserReconciliation = (index: number, field: 'reason' | 'amount', value: string | number) => {
    setFinUserReconciliations(finUserReconciliations.map((rec, i) => 
      i === index ? { ...rec, [field]: value } : rec
    ));
  };

  const loadFinanceInputs = async () => {
    setIsLoading(true);
    try {
      const api = window.sharePointAPI;
      if (!api?.downloadOthIncFinInput) throw new Error('SharePoint API not available');
      const result = await api.downloadOthIncFinInput();
      if (!result.success || !result.data) throw new Error(result.error || 'Failed to download inputs');
      const incoming = Array.isArray(result.data.leases) ? result.data.leases : [];
      const mapped: FinLease[] = incoming.map((lease: any) => ({
        lease_name: lease.lease_name || "",
        commencement_date: lease.commencement_date ? new Date(lease.commencement_date).toISOString().slice(0,10) : "",
        lease_period: typeof lease.lease_period === 'number' ? lease.lease_period : Number(lease.lease_period || 0),
        int_ror: typeof lease.int_ror === 'number' ? lease.int_ror : Number(lease.int_ror || 0),
        payment_cycle: lease.payment_cycle === 'Yearly' ? 'Yearly' : 'Monthly',
        conditions: {
          c_i: (lease.conditions?.c_i === 'Yes' || lease.conditions?.c_i === 'No') ? lease.conditions.c_i : "",
          c_ii: (lease.conditions?.c_ii === 'Yes' || lease.conditions?.c_ii === 'No') ? lease.conditions.c_ii : "",
          c_iii: (lease.conditions?.c_iii === 'Yes' || lease.conditions?.c_iii === 'No') ? lease.conditions.c_iii : "",
          c_iv: (lease.conditions?.c_iv === 'Yes' || lease.conditions?.c_iv === 'No') ? lease.conditions.c_iv : "",
          c_v: (lease.conditions?.c_v === 'Yes' || lease.conditions?.c_v === 'No') ? lease.conditions.c_v : "",
        },
        payment_details: (lease.payment_details || []).map((p: any) => ({
          payment_classification: p.payment_classification || "",
          payment_type: p.payment_type || "",
          payment_amount: Number(p.payment_amount || 0),
          n_payments: Number(p.n_payments || 0),
          int_incr: Array.isArray(p.int_incr) ? p.int_incr.join(',') : (p.int_incr || ''),
          incr_per: Array.isArray(p.incr_per) ? p.incr_per.join(',') : (p.incr_per || ''),
          month_first_payment: Number(p.month_first_payment || 0),
        }))
      }));
      setLeases(mapped);
    } catch (e) {
      alert(`Error downloading Finance Lease: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const payload = useMemo(() => {
    const mapped = leases.map(l => ({
      lease_name: l.lease_name,
      commencement_date: l.commencement_date ? new Date(l.commencement_date).toLocaleDateString('en-US') : "",
      lease_period: typeof l.lease_period === 'number' ? l.lease_period : Number(l.lease_period || 0),
      int_ror: typeof l.int_ror === 'number' ? l.int_ror : Number(l.int_ror || 0),
      payment_cycle: l.payment_cycle || 'Monthly',
      conditions: {
        c_i: l.conditions.c_i || 'No',
        c_ii: l.conditions.c_ii || 'No',
        c_iii: l.conditions.c_iii || 'No',
        c_iv: l.conditions.c_iv || 'No',
        c_v: l.conditions.c_v || 'No',
      },
      payment_details: l.payment_details.map(p => ({
        payment_classification: p.payment_classification || 'Rental',
        payment_type: p.payment_type,
        payment_amount: Number(p.payment_amount || 0),
        n_payments: Number(p.n_payments || 0),
        int_incr: p.int_incr ? p.int_incr.split(',').map(s => s.trim()).filter(Boolean).map(Number) : [],
        incr_per: p.incr_per ? p.incr_per.split(',').map(s => s.trim()).filter(Boolean).map(Number) : [],
        month_first_payment: Number(p.month_first_payment || 0),
      }))
    }));
    return { leases: mapped };
  }, [leases]);

  const uploadJson = async () => {
    setRunning(true);
    try {
      const jsonString = JSON.stringify(payload, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTINC_FinInp.json',
          referenceValue: 'Other Income Finance Lease Input',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTINC_FinInp']
        });
        if (!result.success) {
          console.error('Failed to save Finance Lease JSON:', result.error || 'Unknown error occurred');
          throw new Error(result.error || 'Unknown error occurred');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error uploading Finance Lease JSON:', error);
      throw error;
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    const handler = async () => {
      // 1. Save Finance Lease inputs
      await uploadJson();
      // 2. Save Finance Lease reconciliation
      await saveFinReconciliationData();
      // 3. Save AS 19 Classification
      await saveAs19Classifications();
      // 4. Save shared inference
      await saveInferenceQuestion();
    };
    registerFinanceGlobalSave(handler);
  }, [uploadJson, saveFinReconciliationData, saveAs19Classifications, saveInferenceQuestion]);


  return (
    <div className="space-y-4">
      {/* AS 19 Classification Assessment */}
      {leases.some(lease => lease.lease_name) && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">AS 19 Classification Assessment</CardTitle>
                <p className="text-white/70 text-sm mt-1">
                  Document our considerations for the AS 19 assessment of lease classification as finance lease based on below conditions
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("AS 19 Classification Assessment"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AS 19 Conditions Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-white font-medium py-3 px-4 w-16">Sl. No.</th>
                    <th className="text-left text-white font-medium py-3 px-4">Condition as per AS 19</th>
                  </tr>
                </thead>
                <tbody>
                  {AS19_CONDITIONS.map((condition, index) => (
                    <tr key={condition.id} className="border-b border-white/5">
                      <td className="text-white/80 py-3 px-4">({condition.id})</td>
                      <td className="text-white/80 py-3 px-4">{condition.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Classification Assessment Table */}
            <div className="space-y-4">
              <h4 className="text-white font-medium">Lease Classification Assessment</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10">
                  <thead>
                    <tr className="bg-white/5">
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-left">Lease Name</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (i)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (ii)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (iii)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (iv)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (v)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leases.filter(lease => lease.lease_name).map((lease) => {
                      const classification = as19Classifications.find(c => c.leaseName === lease.lease_name);
                      return (
                        <tr key={lease.lease_name}>
                          <td className="border border-white/10 text-white py-3 px-4">{lease.lease_name}</td>
                          {['conditionI', 'conditionII', 'conditionIII', 'conditionIV', 'conditionV'].map((condition) => (
                            <td key={condition} className="border border-white/10 py-3 px-4">
                              <Select
                                value={classification?.[condition as keyof typeof classification] || 'No'}
                                onValueChange={(value) => updateAs19Classification(lease.lease_name, condition, value)}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border-white/10 bg-black/90 text-white">
                                  <SelectItem value="Yes">Yes</SelectItem>
                                  <SelectItem value="No">No</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Save handled by global Save button in parent */}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-end">
        <Button onClick={addLease} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Lease
        </Button>
      </div>

      {leases.map((lease, li) => (
        <div key={li} className="rounded-md border border-white/10 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-white">Name of Lease</Label>
              <Input value={lease.lease_name} onChange={(e) => updateLeaseField(li, 'lease_name', e.target.value)} className="border-white/10 bg-black/40 text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white">Lease Commencement Date</Label>
              <Input type="date" value={lease.commencement_date} onChange={(e) => updateLeaseField(li, 'commencement_date', e.target.value)} className="border-white/10 bg-black/40 text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white">Lease Period (in months)</Label>
              <Input type="number" value={lease.lease_period as any} onChange={(e) => updateLeaseField(li, 'lease_period', e.target.value ? Number(e.target.value) : '')} className="border-white/10 bg-black/40 text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white">Internal Rate of Return (%)</Label>
              <Input type="number" value={lease.int_ror as any} onChange={(e) => updateLeaseField(li, 'int_ror', e.target.value ? Number(e.target.value) : '')} className="border-white/10 bg-black/40 text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white">Payment Cycle</Label>
              <Select value={lease.payment_cycle as any} onValueChange={(v) => updateLeaseField(li, 'payment_cycle', v as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-white/90 text-sm font-medium">AS 19 Assessment</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/70">
                  <tr>
                    <th className="px-2 py-2 text-left">Lease Name</th>
                    <th className="px-2 py-2 text-left">Condition (i)</th>
                    <th className="px-2 py-2 text-left">Condition (ii)</th>
                    <th className="px-2 py-2 text-left">Condition (iii)</th>
                    <th className="px-2 py-2 text-left">Condition (iv)</th>
                    <th className="px-2 py-2 text-left">Condition (v)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-white/10">
                    <td className="px-2 py-2 text-white/80">{lease.lease_name || '—'}</td>
                    {(['c_i','c_ii','c_iii','c_iv','c_v'] as (keyof FinCondition)[]).map((key) => (
                      <td key={key} className="px-2 py-2">
                        <Select value={lease.conditions[key] as any} onValueChange={(v) => updateCondition(li, key, v as any)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-white/90 text-sm font-medium">Payment Schedule</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/70">
                  <tr>
                    <th className="px-2 py-2 text-left">Payment Classification</th>
                    <th className="px-2 py-2 text-left">Payment Type</th>
                    <th className="px-2 py-2 text-left">Payment Amount</th>
                    <th className="px-2 py-2 text-left">No. of months</th>
                    <th className="px-2 py-2 text-left">Interval of Increment</th>
                    <th className="px-2 py-2 text-left">Increment %</th>
                    <th className="px-2 py-2 text-left">Month of First Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {lease.payment_details.map((r, ri) => {
                    const isRental = r.payment_classification === 'Rental';
                    return (
                      <tr key={ri} className="border-t border-white/10">
                        <td className="px-2 py-2">
                          <Select value={r.payment_classification as any} onValueChange={(v) => updatePaymentRow(li, ri, 'payment_classification', v as any)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-black/90 text-white">
                              <SelectItem value="Rental">Rental</SelectItem>
                              <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                              <SelectItem value="Unguaranteed Residual Value">Unguaranteed Residual Value</SelectItem>
                              <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2"><Input value={r.payment_type} onChange={(e) => updatePaymentRow(li, ri, 'payment_type', e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                        <td className="px-2 py-2"><Input type="number" value={r.payment_amount as any} onChange={(e) => updatePaymentRow(li, ri, 'payment_amount', e.target.value ? Number(e.target.value) : '')} className="border-white/10 bg-black/40 text-white" /></td>
                        <td className="px-2 py-2"><Input type="number" disabled={!isRental} value={r.n_payments as any} onChange={(e) => updatePaymentRow(li, ri, 'n_payments', e.target.value ? Number(e.target.value) : '')} className="border-white/10 bg-black/40 text-white" /></td>
                        <td className="px-2 py-2"><Input placeholder="e.g., 10,15,18" disabled={!isRental} value={r.int_incr} onChange={(e) => updatePaymentRow(li, ri, 'int_incr', e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                        <td className="px-2 py-2"><Input placeholder="e.g., 2,3,5" disabled={!isRental} value={r.incr_per} onChange={(e) => updatePaymentRow(li, ri, 'incr_per', e.target.value)} className="border-white/10 bg-black/40 text-white" /></td>
                        <td className="px-2 py-2"><Input type="number" disabled={!isRental} value={r.month_first_payment as any} onChange={(e) => updatePaymentRow(li, ri, 'month_first_payment', e.target.value ? Number(e.target.value) : '')} className="border-white/10 bg-black/40 text-white" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-white/60">Note: The interval of increments and the increment % have to be separated by single commas. For eg. if the increment happens in the 10th, 15th and 18th months by 2%, 3% and 5% respectively then they should be entered as 10,15,18 and 2,3,5 respectively for that type of payment.</div>
            <Button onClick={() => addPaymentRow(li)} size="sm" variant="outline" className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
              <Plus className="h-4 w-4 mr-2" /> Add Row
            </Button>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <Button
          onClick={async () => {
            if (finIsRunningLease) return;
            setFinIsRunningLease(true);
            try {
              // First save the latest Finance Lease input JSON
              await uploadJson();

              const result = await window.payroll.run("execute_othinc_finance_lease", {
                inputFiles: [],
                options: { reference_value: "Other Income Finance Lease" },
              });
              if (result.ok) {
                alert(' Finance Lease run completed successfully! Excel uploaded to SharePoint.');
              } else {
                alert(result.error || 'Failed to start Finance Lease run');
              }
            } catch (e) {
              alert(`Error starting Finance Lease run: ${e instanceof Error ? e.message : 'Unknown error'}`);
            } finally {
              setFinIsRunningLease(false);
            }
          }}
          disabled={finIsRunningLease}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {finIsRunningLease ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <FileCheck2 className="h-4 w-4 mr-2" />
              Run Finance Lease
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Upload and Run Finance Lease"))}
          className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
          title="Review Comment"
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
      </div>

      {/* Finance Lease Liability Reconciliation */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Table className="h-5 w-5" />
              Finance Lease Liability Reconciliation
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={loadFinReconciliationData}
                disabled={finIsLoadingReconciliation}
                size="sm"
                variant="outline"
                className="border-white/10 text-white"
              >
                <Play className="h-4 w-4 mr-2" />
                {finIsLoadingReconciliation ? 'Loading...' : 'Execution'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Finance Lease Liability Reconciliation"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">Finance Lease liability as per workings (A)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <Input
                      type="number"
                      value={finLiabilityAsPerWorkings || ''}
                      onChange={(e) => setFinLiabilityAsPerWorkings(parseFloat(e.target.value) || 0)}
                      className="border-white/10 bg-black/40 text-white text-right"
                      placeholder="Enter amount"
                    />
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">Finance Lease liability as per schedule (B)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <div className="space-y-2">
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (value && !finSelectedLedgerAccounts.includes(parseInt(value))) {
                            setFinSelectedLedgerAccounts([...finSelectedLedgerAccounts, parseInt(value)]);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={finIsLoadingLedgerAccounts ? "Loading..." : "Select ledger accounts"} />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white max-h-60">
                          {finAvailableLedgerAccounts
                            .filter(acc => !finSelectedLedgerAccounts.includes(acc.ledger_id))
                            .map((account) => (
                              <SelectItem key={account.ledger_id} value={account.ledger_id.toString()}>
                                {account.ledger_name} (Note Line ID: {account.note_line_id})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {finSelectedLedgerAccounts.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {finSelectedLedgerAccounts.map((ledgerId) => {
                            const account = finAvailableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
                            return account ? (
                              <div key={ledgerId} className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-sm text-white">
                                <span>{account.ledger_name}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setFinSelectedLedgerAccounts(finSelectedLedgerAccounts.filter(id => id !== ledgerId))}
                                  className="h-4 w-4 p-0 text-white hover:text-red-400"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}
                      <div className="text-right text-white font-semibold">
                        Total: {getFinTotalClosingBalance().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference (C)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getFinDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                {finUserReconciliations.map((rec, index) => (
                  <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      <Input
                        type="text"
                        value={rec.reason}
                        onChange={(e) => updateFinUserReconciliation(index, 'reason', e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="Enter reason"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={rec.amount || ''}
                          onChange={(e) => updateFinUserReconciliation(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="border-white/10 bg-black/40 text-white text-right flex-1"
                          placeholder="Enter amount"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFinUserReconciliation(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getFinNetDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-start items-center">
            <Button
              onClick={addFinUserReconciliation}
              size="sm"
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="other-income"
            itemId="NET-INVESTMENT-IN-LEASES-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Operating Lease")]: "Operating Lease",
              [getSectionQuestionId("Operating Lease Liability Reconciliation")]: "Operating Lease Liability Reconciliation",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

